// PartyCoverageTab.tsx — SQ3 · ③.4 (2026-07-25 spec §5.4).
//
// Read-only coverage-gap matrix for a reference-party layer (PLUMBER / ARCHITECT
// / AFFILIATE), reusing the same green/yellow/red state → district → pincode
// drill as the Dealer-Territory tab. Bound to the layer-generic
// coverage-matrix-v2?layer=<PARTY> + coverage-drill?layer=<PARTY> endpoints.
//
// Unlike the Dealer tab there is NO assignment/proximity UI here — a party owns
// no leads. Coverage is grown either bottom-up (inline party-create seeds one
// pincode) or top-down via the assignment authoring wizard. This tab visualises
// where each party type currently has coverage and where the gaps are.

import { useEffect, useMemo, useState } from 'react';
import { MapPin, ChevronRight, RefreshCcw, ArrowLeft, AlertCircle } from 'lucide-react';
import {
  leadRouterV2Api,
  type CoverageMatrixV2Response,
  type CoverageDrillV2,
  type CoverageLayerParam,
} from '@/api/leadRouter';

const TONE_BG: Record<string, string> = {
  green: 'var(--forest-100, #d6ecd9)',
  yellow: 'var(--warn-bg, #f7eccf)',
  red: 'repeating-linear-gradient(45deg, #f6d5d5, #f6d5d5 5px, #fbe4e4 5px, #fbe4e4 10px)',
};
const TONE_FG: Record<string, string> = {
  green: 'var(--forest, #2f7d3b)',
  yellow: 'var(--warn, #a5741a)',
  red: 'var(--danger, #b5433a)',
};
const TONE_GLYPH: Record<string, string> = { green: '●', yellow: '◐', red: '—' };

interface DrillFrame { productGroupKey: string; state: string; district?: string }

export function PartyCoverageTab({ layer, label }: { layer: CoverageLayerParam; label: string }) {
  const [matrix, setMatrix] = useState<CoverageMatrixV2Response | null>(null);
  const [drillData, setDrillData] = useState<CoverageDrillV2 | null>(null);
  const [drill, setDrill] = useState<DrillFrame | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    setLoading(true); setError(null);
    try {
      setMatrix(await leadRouterV2Api.getCoverageMatrixV2(layer));
    } catch (e: any) {
      setError(e?.response?.data?.error ?? e?.message ?? `Failed to load ${label} coverage`);
    } finally { setLoading(false); }
  };
  useEffect(() => { void load(); }, [layer]);

  const openDrill = async (frame: DrillFrame) => {
    setDrill(frame);
    try {
      setDrillData(await leadRouterV2Api.getCoverageDrill({ layer, ...frame }));
    } catch (e: any) {
      setError(e?.response?.data?.error ?? e?.message ?? 'Failed to drill');
    }
  };

  const totals = useMemo(() => {
    const t: Record<string, number> = { green: 0, yellow: 0, red: 0 };
    for (const c of matrix?.cells ?? []) t[c.cell] = (t[c.cell] ?? 0) + 1;
    return t;
  }, [matrix]);

  if (loading) return <div style={{ padding: 20, color: 'var(--ink-3)' }}>Loading {label} coverage…</div>;
  if (error) {
    return (
      <div data-testid="party-coverage-error" style={{ display: 'flex', gap: 8, color: 'var(--danger)', padding: 16 }}>
        <AlertCircle size={16} /> <span>{error}</span>
        <button type="button" onClick={() => void load()} style={{ marginLeft: 8 }}><RefreshCcw size={13} /> Retry</button>
      </div>
    );
  }

  // Drill view: districts (or pincodes) of one state.
  if (drill && drillData) {
    return (
      <div data-testid={`party-coverage-drill-${layer}`}>
        <button type="button" onClick={() => { setDrill(null); setDrillData(null); }}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12.5, color: 'var(--ink-2)', border: 'none', background: 'transparent', cursor: 'pointer', marginBottom: 12 }}>
          <ArrowLeft size={13} /> Back to {label} states
        </button>
        <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 10 }}>
          {label} coverage — {drill.state}{drill.district ? ` › ${drill.district}` : ''}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 8 }}>
          {drillData.cells.map((c) => (
            <button key={c.unit} type="button"
              disabled={drillData.level !== 'district'}
              onClick={() => drillData.level === 'district' && void openDrill({ ...drill, district: c.unit })}
              style={{
                textAlign: 'left', border: '1px solid var(--line)', borderRadius: 8, padding: '8px 10px',
                background: TONE_BG[c.cell], color: TONE_FG[c.cell], cursor: drillData.level === 'district' ? 'pointer' : 'default',
              }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 600 }}>
                <span>{c.unit}</span><span>{TONE_GLYPH[c.cell]}</span>
              </div>
              <div style={{ fontSize: 11, opacity: 0.8 }}>{c.ownedSub}/{c.totalSub} covered</div>
            </button>
          ))}
        </div>
      </div>
    );
  }

  // State matrix (product group × state).
  const groups = matrix?.groups ?? [];
  const states = matrix?.states ?? [];
  const cellOf = (g: string, s: string) => matrix?.cells.find((c) => c.productGroupKey === g && c.state === s);

  return (
    <div data-testid={`party-coverage-${layer}`}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
        <MapPin size={16} style={{ color: 'var(--ink-2)' }} />
        <div style={{ fontSize: 13, color: 'var(--ink-2)' }}>
          {label} pincode coverage — green: fully covered, yellow: partial, red: gap.
          Coverage feeds reference-suggestion + gap visibility only (parties never receive routed leads).
        </div>
        <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--ink-3)' }}>
          ● {totals.green}  ◐ {totals.yellow}  — {totals.red}
        </span>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ borderCollapse: 'collapse', fontSize: 12, width: '100%' }}>
          <thead>
            <tr>
              <th style={{ position: 'sticky', left: 0, background: 'var(--ivory-0)', textAlign: 'left', padding: 6 }}>State</th>
              {groups.map((g) => <th key={g} style={{ padding: 6, fontWeight: 600 }}>{g}</th>)}
            </tr>
          </thead>
          <tbody>
            {states.map((s) => (
              <tr key={s}>
                <td style={{ position: 'sticky', left: 0, background: 'var(--ivory-0)', padding: 6, fontWeight: 500 }}>{s}</td>
                {groups.map((g) => {
                  const c = cellOf(g, s);
                  const tone = c?.cell ?? 'red';
                  return (
                    <td key={g} style={{ padding: 3 }}>
                      <button type="button" onClick={() => void openDrill({ productGroupKey: g, state: s })}
                        style={{ width: '100%', border: '1px solid var(--line)', borderRadius: 6, padding: '5px 6px', background: TONE_BG[tone], color: TONE_FG[tone], cursor: 'pointer', display: 'flex', justifyContent: 'space-between' }}>
                        <span>{TONE_GLYPH[tone]}</span>
                        <ChevronRight size={11} style={{ opacity: 0.5 }} />
                      </button>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
